import { cloneDeep } from 'lodash';
import React, { FC, useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Timestamp } from 'api/models';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import { TableSkeleton } from 'components/atoms/table';
import { weekFormatter } from 'components/organisms/timesheets/common/utils';
import { EmptyState } from '../component';
import {
  calenderActivityActions,
  calenderActivityReducer,
} from './activity-reducer';
import {
  ActivityTable,
  CalenderIntegrationRowDataWithMeta,
  SelectedActivityRowData,
  useGrid,
} from './activity-table';
import { EventWrapper, NoMoreEvent } from './component';
import { ReactComponent as NoEventIcon } from './no-event.svg';

const Loading = () => (
  <div>
    <TableSkeleton rowCount={3} />
  </div>
);

const EmptyEvent = ({ startDate, refresh }) => (
  <EmptyState
    image={<NoEventIcon />}
    title={<FormattedMessage id="timesheets.integration.no-event" />}
    message={
      <FormattedMessage
        id="timesheets.integration.no-event.body"
        values={{
          dateRange: weekFormatter(startDate),
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />
    }
    primaryCTA={
      <EmptyState.CallToAction.Secondary onClick={refresh}>
        <FormattedMessage id="refresh" />
      </EmptyState.CallToAction.Secondary>
    }
  />
);

interface SelectEventProps {
  eventsWithMeta?: CalenderIntegrationRowDataWithMeta[];
  isLoading: boolean;
  onSelect: (rows: SelectedActivityRowData[]) => void;
  startDate: Timestamp;
  refresh: () => void;
  projects?: Project[];
  mandateCategory: boolean;
  timeEntryCategories: Category[];
}

const TableWrapper = styled.div`
  padding-bottom: var(--spacing-06);
`;

export const AddActivity: FC<SelectEventProps> = ({
  eventsWithMeta,
  onSelect,
  isLoading,
  startDate,
  refresh,
  projects,
  mandateCategory,
  timeEntryCategories,
}) => {
  const [state, dispatch] = useReducer(calenderActivityReducer, {
    data: undefined,
  });
  const { onGridReady, gridApi } = useGrid();

  useEffect(() => {
    dispatch(calenderActivityActions.initialize(eventsWithMeta));
    gridApi?.refreshCells({ force: true }); // since data change in state doesn't not refresh ag grid we are manually refreshing cells
  }, [eventsWithMeta, gridApi]);

  useEffect(() => {
    const selectedRows: SelectedActivityRowData[] = [];

    state.data?.forEach(([, rows]) => {
      selectedRows.push(
        ...(rows.filter(
          (row) => row.isSelected && row.projects,
        ) as SelectedActivityRowData[]),
      );
    });
    onSelect(selectedRows);
  }, [onSelect, state.data]);

  if (isLoading) {
    return <Loading />;
  }

  if (!state.data?.length) {
    return <EmptyEvent startDate={startDate} refresh={refresh} />;
  }

  return (
    <EventWrapper>
      {state.data.map(([meta, rows]) => (
        <TableWrapper>
          <ActivityTable
            key={meta.mergeStartEpoch}
            rowData={cloneDeep(rows)} // TODO: data from redux don't allow grid to make an update
            calenderMeta={cloneDeep(meta)}
            onChange={(row) => {
              dispatch(calenderActivityActions.updateActivity(row));
            }}
            onGridReady={onGridReady}
            projects={projects}
            mandateCategory={mandateCategory}
            timeEntryCategories={timeEntryCategories}
          />
        </TableWrapper>
      ))}
      <NoMoreEvent>
        <FormattedMessage id="timesheets.integrations.event.end" />
      </NoMoreEvent>
    </EventWrapper>
  );
};
